import { useCallback } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { ROLE_PERMISSIONS_PAGE_LIMIT, rolesEndpoints as endpoints } from '@/api/endpoints';
import { callGet } from '@/api/fetcher';
import { getPermissions } from '@/redux/permissions/selectors';
import { selectAccountId } from '@/redux/session/selectors';
import { actions } from '@/shared/types/permissions';
import { RolePermission, RolePermissions } from '@/shared/types/role';
import { RootState } from '@/shared/types/state';

/** Stable identity, so callers can memoise on it. */
const NO_PERMISSIONS: RolePermission[] = [];

const API = {
  listRolePermissions: async (role_id: string): Promise<RolePermissions> => {
    const data: RolePermissions = await callGet(endpoints.rolePermissions(role_id));

    if (data.permissions?.length >= ROLE_PERMISSIONS_PAGE_LIMIT) {
      console.error(
        `Role ${role_id} returned ${ROLE_PERMISSIONS_PAGE_LIMIT} permissions, so the list is cut and cannot be edited safely`
      );
    }

    return data;
  },
};

export const rolePermissionsQueryKey = (roleId: string, accountId: string) => [
  endpoints.rolePermissions(roleId),
  accountId,
];

export const usePrefetchRolePermissions = () => {
  const queryClient = useQueryClient();
  const accountId = useSelector(selectAccountId);
  const canRead = useSelector((state: RootState) => getPermissions(state, 'roles', actions.READ));

  return useCallback(
    (roleId: string) => {
      if (!accountId || !roleId || !canRead) {
        return;
      }
      queryClient.prefetchQuery({
        queryKey: rolePermissionsQueryKey(roleId, accountId),
        queryFn: () => API.listRolePermissions(roleId),
      });
    },
    [accountId, canRead, queryClient]
  );
};

/** Pass no id to keep the request idle. */
export const useRolePermissions = (roleId?: string) => {
  const accountId = useSelector(selectAccountId);
  const canRead = useSelector((state: RootState) => getPermissions(state, 'roles', actions.READ));

  const {
    data,
    status: listStatus,
    fetchStatus,
  } = useQuery<RolePermissions, Error>({
    queryKey: rolePermissionsQueryKey(roleId ?? '', accountId),
    queryFn: () => API.listRolePermissions(roleId as string),
    enabled: !!accountId && !!roleId && canRead,
  });

  const permissions = data?.permissions ?? NO_PERMISSIONS;

  return {
    permissions,
    hasData: data !== undefined,
    listStatus,
    isTruncated: permissions.length >= ROLE_PERMISSIONS_PAGE_LIMIT,
    isLoading: listStatus === 'pending' && fetchStatus !== 'idle',
  };
};
